import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useFilters, usePagination, useSortBy, useTable} from 'react-table';
import '../../style/Table.less';
import TextInput from '../form/TextInput';
import PopoverButton from '../popover/PopoverButton';
import EmptyResultMessage from './EmptyResultMessage';
import Pagination from './Pagination';

function Table({
                   columns,
                   data,
                   fetchData,
                   getColumnProps = () => ({}),
                   rowClick = () => ({}),
                   popup,
                   textFilter,
                   pageCount: controlledPageCount,
                   pageSize: defaultPageSize,
                   dataInitialized,
                   initialSort,
                   dataCy,
                   filter, //Set pagination filters outside of table here (used to set page index to 0, if filters change)
                   filterFromOutside = [],
                   onFilterChange = null,
                   filterable = false, // for manual filtering
                   sortable = true, // for manual sorting
                   isFiltered,
                   onSortChange,
                   sortFromOutside = null,
                   resetPageIndex,
                   defaultFilterClass
               }) {

    const [t] = useTranslation();
    const defaultColumn = React.useMemo(
        () => ({
            Filter: defaultColumnFilter
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setSortBy,
        setFilter,
        // Get the state from the instance
        state: {
            pageIndex,
            pageSize,
            sortBy
        }
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            disableFilters: !filterable,
            disableSortBy: !sortable,
            autoResetSortBy: false,
            autoResetFilters: false,
            initialState: {
                pageIndex: 0,
                pageSize: (defaultPageSize || fetchData) ? defaultPageSize : 25,
                sortBy: initialSort ? initialSort : sortFromOutside ? sortFromOutside : []
            }, // Pass our hoisted table state
            manualPagination: !!fetchData, // Tell the usePagination
            manualSortBy: !!fetchData,
            // hook that we'll handle our own data fetching
            // This means we'll also have to provide our own
            // pageCount.,
            manualFilters: !!fetchData,
            pageCount: fetchData ? controlledPageCount : -1
        },
        useFilters,
        useSortBy,
        usePagination
    );

    //Goes to first page, if filter outside table changes
    React.useEffect(() => {
        if (filter && pageIndex !== filter.page) {
            gotoPage(filter.page);
        }
        if (pageIndex >= pageCount) {
            if (resetPageIndex) {
                resetPageIndex();
            }
            gotoPage(0);
        }
    }, [filter]);

    React.useEffect(() => {
        filterFromOutside.forEach(rowFilter => setFilter(rowFilter.row, rowFilter.value));
    }, [filterFromOutside]);

    React.useEffect(() => {
        if (sortFromOutside && sortFromOutside[0] && sortFromOutside[0].id && sortBy[0] && sortBy[0].id !== sortFromOutside[0].id) {
            setSortBy(sortFromOutside);
        }
    }, [sortFromOutside]);

    function defaultColumnFilter({
                                     column: {
                                         filterValue,
                                         setFilter: changeFilter,
                                         id
                                     }
                                 }) {
        return (
            <div className={defaultFilterClass ? 'filter-text-box ' + defaultFilterClass : 'filter-text-box'}>
                <TextInput
                    value={filterValue || ''}
                    onChange={value => {
                        if (onFilterChange) {
                            onFilterChange(id, value);
                        }
                        changeFilter(value || undefined);
                    }}
                    placeholder={t('Table.SEARCH')}
                    onClick={e => e.stopPropagation()}
                />
            </div>
        );
    }

    React.useEffect(() => {
        if (onSortChange) {
            onSortChange(sortBy);
        }
    }, [sortBy]);

    // Listen for changes in pagination and use the state to fetch our new data
    React.useEffect(() => {
        if (fetchData) {
            fetchData({
                pageIndex,
                pageSize,
                sortBy: sortBy[0] ? sortBy[0].id : undefined,
                isDescending: sortBy[0] ? sortBy[0].desc : undefined
            });
        }
    }, [pageIndex, pageSize]);

    React.useEffect(() => {
        // only fetch data if sorting was changed on first page. If sorting changes on other page, manually switch to first page
        if (fetchData && pageIndex === 0) {
            fetchData({
                pageIndex,
                pageSize,
                sortBy: sortBy[0] ? sortBy[0].id : undefined,
                isDescending: sortBy[0] ? sortBy[0].desc : undefined
            });
        }
    }, [sortBy]);

    // Show message if loaded results are empty
    if (dataInitialized && (!data || data.length === 0)) {
        return <EmptyResultMessage/>;
    }

    const getSortingStyle = (isSorted, isSortedDesc) => {
        if (isSorted && isSortedDesc) {
            return 'col-sort-desc';
        } else if (isSorted && !isSortedDesc) {
            return 'col-sort-asc';
        } else {
            return '';
        }
    };

    // Render the UI for your table
    return (
        <>
            <table {...getTableProps()} className="table d-inline-block d-md-table overflow-auto" data-cy={dataCy}>
                <thead className="thead-light">
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column, idx) => (
                            <th scope="col"
                                width={column.width}
                                className={getSortingStyle(column.isSorted, column.isSortedDesc)}
                                {...column.getHeaderProps([
                                    getColumnProps(column)
                                ])}
                            >
                                <div className="row align-items-center">
                                    {
                                        column.isSorted &&
                                        <div className="sort-indicator mr-2 ml-2 small">
                                            {
                                                column.isSortedDesc
                                                    ? <FontAwesomeIcon icon="arrow-down"/>
                                                    : <FontAwesomeIcon icon="arrow-up"/>
                                            }
                                        </div>
                                    }
                                    <span className="th-text" {...column.getSortByToggleProps()}>{column.render('Header')}</span>
                                    {
                                        !!popup && !!popup[idx] &&
                                        <div className="">
                                            {/*<h6 className="bold inline-row">{column.render('Header')}</h6>*/}
                                            <PopoverButton
                                                content={popup[idx]}
                                                icon={'chevron-down'}
                                                placement={'bottom'}
                                                className={'btn btn-transparent-secondary btn-icon ml-2'}
                                                // className={isFiltered && isFiltered[idx] ? "btn btn-icon ml-2 btn-lightblue" : "btn btn-transparent-secondary btn-icon ml-2"}
                                                buttonIcon={<FontAwesomeIcon icon="filter" className={isFiltered && isFiltered[idx] ? 'lightblue' : ''}/>}
                                            />
                                        </div>
                                    }
                                    {
                                        column.canFilter &&
                                        <div className="text-filter mt-2">{column.render('Filter')}</div>
                                    }
                                    {
                                        !!textFilter && !!textFilter[idx] &&
                                        <div className="text-filter mt-2">
                                            <div className="filter-text-box">
                                                {textFilter[idx]}
                                            </div>
                                        </div>
                                    }

                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()} onClick={() => {
                            rowClick(row.original.id);
                        }}>
                            {row.cells.map(cell => {
                                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                            })}
                        </tr>
                    );
                })}
                </tbody>
                <tfoot>
                {/*footerGroups.map(group => (
                    <tr {...group.getFooterGroupProps()}>
                        {group.headers.map(column => (
                            <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                        ))}
                    </tr>
                ))*/}
                </tfoot>
            </table>
            {
                pageOptions.length > 1 &&
                <Pagination
                    canNextPage={canNextPage}
                    canPreviousPage={canPreviousPage}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    pageCount={pageCount}
                    pageIndex={pageIndex}
                    previousPage={previousPage}
                />
            }
        </>
    );
}

export default Table;
